import type { VtkModules } from './loadVtkModules';

export type CameraState = {
  pos: number[];
  fp: number[];
  up: number[];
  cr: number[];
};

// Camera presets — tweak these numbers to adjust zoom positions.
// (pos, fp, up, cr) = (position, focalPoint, viewUp, clippingRange)
export const STENOSIS_CAMERA: CameraState = {
  pos: [0.11232480170925391, 0.07468209890072405, 1.0313387411727133],
  fp: [0.1698582351076436, 0.032397661604180665, 1.1229690719941223],
  up: [0.2574445358994847, 0.9286965751814913, 0.26691755690037994],
  cr: [0.040651030851088565, 0.20797447522862902],
};

export const BIFURCATION_CAMERA: CameraState = {
  pos: [0.27116899469311706, 0.11472752364487214, 1.1325578573365134],
  fp: [0.22198761809352419, 0.027629137241854035, 1.0996874674186525],
  up: [0.6992942554999423, -0.5617166058358573, 0.44211084464535094],
  cr: [0.037675187746104344, 0.19659747098976377],
};

export type VtkScene = {
  renderWindow: unknown;
  renderer: unknown;
  openGLRenderWindow: unknown;
  interactor: unknown;

  mapper: unknown;
  actor: unknown;
  polydata: unknown;

  streamlineMapper: unknown;
  streamlineActor: unknown;
  streamlinePolydata: unknown;

  scalarBarActor: unknown | null;

  destroy: () => void;
  resize: () => void;
};

export function createVtkScene(
  vtk: VtkModules,
  container: HTMLDivElement,
): VtkScene {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderWindow = (vtk.vtkRenderWindow as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderer = (vtk.vtkRenderer as any).newInstance({
    background: [0.1, 0.1, 0.1],
  });
  renderWindow.addRenderer(renderer);

  // Camera angle/zoom set by applyDatasetToScene (resetCamera + zoom)
  // Default orientation before data loads — pulled back for full-model view:
  const cam = renderer.getActiveCamera();
  cam.setPosition(-2.9, 1.15, -1.6);
  cam.setFocalPoint(0.176, 0.03, 1.106);
  cam.setViewUp(-0.1, 0.968, 0.231);
  cam.setClippingRange(0.5, 5);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openGLRenderWindow = (vtk.vtkOpenGLRenderWindow as any).newInstance();
  openGLRenderWindow.setContainer(container);
  renderWindow.addView(openGLRenderWindow);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const interactor = (vtk.vtkInteractor as any).newInstance();
  interactor.setView(openGLRenderWindow);
  interactor.initialize();
  // Prevent browser default on pointer events so right-click isn't swallowed
  interactor.setPreventDefaultOnPointerDown(true);
  interactor.setPreventDefaultOnPointerUp(true);

  // Right-click pan: VTK freezes style Object, so implement manually on container
  // Register BEFORE bindEvents with capture:true so we fire before VTK's handler
  let rightPanning = false;
  let lastPanX = 0;
  let lastPanY = 0;

  container.addEventListener(
    'pointerdown',
    (e) => {
      if (e.button === 2) {
        rightPanning = true;
        lastPanX = e.clientX;
        lastPanY = e.clientY;
        container.setPointerCapture(e.pointerId);
        e.preventDefault();
        e.stopImmediatePropagation(); // Block VTK's handler for right-click
      }
    },
    { capture: true },
  );

  container.addEventListener('pointermove', (e) => {
    if (!rightPanning) return;

    const dx = e.clientX - lastPanX;
    const dy = e.clientY - lastPanY;
    lastPanX = e.clientX;
    lastPanY = e.clientY;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cam = (renderer as any).getActiveCamera();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const view = openGLRenderWindow as any;

    const fp = cam.getFocalPoint();
    const fpDisplay = view.worldToDisplay(...fp, renderer);
    const newDisplay = [fpDisplay[0] + dx, fpDisplay[1] - dy, fpDisplay[2]];
    const newWorld = view.displayToWorld(...newDisplay, renderer);
    cam.setFocalPoint(...newWorld);
    const pos = cam.getPosition();
    cam.setPosition(
      pos[0] + newWorld[0] - fp[0],
      pos[1] + newWorld[1] - fp[1],
      pos[2] + newWorld[2] - fp[2],
    );

    renderWindow.render();
  });

  const onPointerUp = (e: PointerEvent) => {
    if (rightPanning) {
      rightPanning = false;
      container.releasePointerCapture(e.pointerId);
    }
  };
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('pointercancel', onPointerUp);

  // Prevent browser context menu (supplementary to VTK's own listener)
  container.addEventListener('contextmenu', (e) => e.preventDefault());

  interactor.bindEvents(container);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const style = (vtk.vtkInteractorStyle as any).newInstance();

  interactor.setInteractorStyle(style);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapper = (vtk.vtkMapper as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actor = (vtk.vtkActor as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polydata = (vtk.vtkPolyData as any).newInstance();

  mapper.setInputData(polydata);
  actor.setMapper(mapper);
  renderer.addActor(actor);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlineMapper = (vtk.vtkMapper as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlineActor = (vtk.vtkActor as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlinePolydata = (vtk.vtkPolyData as any).newInstance();

  // removing shadows and other visual effects on streamlines
  const streamlineProp = streamlineActor.getProperty();
  streamlineProp.setAmbient(1);
  streamlineProp.setDiffuse(0);
  streamlineProp.setSpecular(0);
  streamlineProp.setSpecularPower(0);

  streamlineMapper.setInputData(streamlinePolydata);
  streamlineActor.setMapper(streamlineMapper);
  streamlineActor.getProperty().setLineWidth(2);
  streamlineActor.setVisibility(false);
  renderer.addActor(streamlineActor);

  const resize = () => {
    const rect = container.getBoundingClientRect();
    openGLRenderWindow.setSize(
      Math.max(1, Math.floor(rect.width)),
      Math.max(1, Math.floor(rect.height)),
    );
    renderWindow.render();
  };

  resize();

  return {
    renderWindow,
    renderer,
    openGLRenderWindow,
    interactor,

    mapper,
    actor,
    polydata,

    streamlineMapper,
    streamlineActor,
    streamlinePolydata,

    scalarBarActor: null,

    resize,
    destroy: () => {
      try {
        interactor.unbindEvents();
      } catch (e) {
        // Ignore errors during unbinding
      }

      try {
        [
          actor,
          mapper,
          polydata,
          streamlineMapper,
          streamlineActor,
          streamlinePolydata,
          openGLRenderWindow,
          interactor,
          renderer,
          renderWindow,
        ].forEach((obj) => obj?.delete?.());
      } catch (e) {
        // Ignore errors during cleanup
      }
    },
  };
}
